import store from "../store";
import ImageContext from "./imageContext";
import { Manufacturers, ScanTypes, SeriesTypes, EyeTypes, OCTBrowserType, DEFAULT_VIEW_TYPE } from "../config/config";
import SpectralisContext from "./spectralisContext";
import CirrusContext from "./cirrusContext";
import RasterContext from "./rasterContext";
import SpectralisComparisonBrowserContext from "./spectralisComparisonBrowserContext";
import CirrusComparisonBrowserContext from "./cirrusComparisonBrowserContext";
import moment from "moment";
import HvfContext from "./hvfContext";
import HvfMultiviewContext from "./hvfMultiviewContext";
import { seriesTimeFormat } from "../helpers/dateUtils";
import { getsortedImages } from "../helpers/utils.js"
import { setOCTButtonText } from "./octUtils";

export function generateContextForImage(imgKey, isSideBySide) {
  let image = store.getters.imageByKey(imgKey);
  if (!image) return null;
  let exam = store.getters.examBySrNo(image.exSrNo);
  if (!exam) return null;

  let device = store.getters.devices.find(d => d.devSrNo == exam.devSrNo);
  let description = device.description.toLowerCase();
  let context = null;
  if (!isSideBySide) {
    if ((description.includes("oct") || description.includes("scans")))
      context = generateOCTContext(exam, image.imgEye, image.imgGroup, imgKey);
    else if (description.includes("visual field") && (image.imgEye == "OD" || image.imgEye == "OS"))
    {
      if(store.getters.defaultHVFVIEW == DEFAULT_VIEW_TYPE.multiView)
        return new HvfMultiviewContext(exam);

      return new HvfContext(exam, image);
    } 
    if (context) return context;
  }
  
  let images = getsortedImages(store.getters.imagesByExSrNo(image.exSrNo), store.getters.imagesOrderByExSrNo(exam.exSrNo)); 
  return new ImageContext(image, images, exam);
}

export function generateContextFromExam(exSrNo) {
  let exam = store.getters.examBySrNo(exSrNo);
  return generateOCTContext(exam);
}

export function generateContextFromGroupName(groupName) {
  let exam = store.getters.exam;
  let eye = store.getters.currentEye;
  return generateOCTContext(exam, eye, groupName);
}

export function generateContextFromGroupAndExSrNo(groupName, exSrNo) {
  let obj = {groupName: groupName, exSrNo: exSrNo};
  let exam = store.getters.examBySrNo(obj.exSrNo);
  let eye = store.getters.currentEye;
  return generateOCTContext(exam, eye, groupName);
}

export function generateContextFromEye(eye) {
  let exam = store.getters.exam;
  let patientExams = store.getters.octExams;

  patientExams.forEach(exam => {
    setOCTButtonText(exam, eye);
  });

  return generateOCTContext(exam, eye);
}

function generateOCTContext(exam, eye, groupName, imgKey) {
  if (exam && exam.isOCT && exam.isScans) {
    let group = getOCTGroup(exam, eye, groupName);
    return generateOCTContextFromGroup(group, exam, imgKey);
  }
  return null;
}

export function generateOCTContextFromGroup(group, exam, imgKey) {
  switch (group && group.browserType) {
    case OCTBrowserType.Cirrus: return new CirrusContext(group, exam);
    case OCTBrowserType.Spectralis: return new SpectralisContext(group, exam, imgKey);
    case OCTBrowserType.Raster: return new RasterContext(group, exam, imgKey);
    default: return null;
  }
}

export function generateMultiviewFromContext(context, launchOption) {
  return generateMultiviewContext(context.exam, context.eye, [context.browserType], undefined, launchOption, context.group, context.imgKey);
}

export function generateMultiviewFromEye(context, eye) {
  if (!context || !context.browserType || !context.isComparisonContext) return null;
  if (context.eye === eye) return context;
  return generateMultiviewContext(context.compare.exam, eye, context.browserType, context.preferredBrowserType, null, context.compare.group);
}

function generateMultiviewContext(exam, eye, browserTypes, preferredBrowserType, launchOption, group, imgKey) {
  let groups = store.getters.groups(exam, eye, browserTypes, preferredBrowserType);
  if (!groups || !groups.length) return null;

  let browserType = preferredBrowserType || browserTypes[0];
  let scanTypeText;

  switch (browserType) {
    case OCTBrowserType.Spectralis:
      scanTypeText = group.scanType.charAt(0).toUpperCase() + group.scanType.slice(1);
      if (scanTypeText == "Star")
        scanTypeText = "Radial";
      return new SpectralisComparisonBrowserContext(exam, eye, launchOption, scanTypeText, group.scanType, imgKey);
    case OCTBrowserType.Cirrus:
    case OCTBrowserType.Raster:
      return new CirrusComparisonBrowserContext(exam, eye, browserType, launchOption, imgKey);
    default: return null;
  }
}

export function getManufacturer(description) {
  if (description.includes("spectralis")) return Manufacturers.Heidelberg;
  if (description.includes("cirrus")) return Manufacturers.Zeiss;
  if (description.includes("optovue")) return Manufacturers.Optovue;
  return Manufacturers.Unknown;
}

export function processScanAndSeriesTypes(manufacturer, groups, examMoment, eye) {
  return groups.map(g => {
    let groupSeriesType = (g.groupSeriesType || "").toLowerCase();
    let recognized = recognizedGroupSeriesType(groupSeriesType);
    let { scanType, seriesType } = getScanAndSeriesTypes(manufacturer, groupSeriesType);
    let browserType = getOCTBrowserType(manufacturer, scanType, seriesType);
    return {
      groupName: g.groupName,
      groupSeriesType: g.groupSeriesType,
      seriesTime: g.seriesTime,
      seriesMoment: g.seriesTime && moment(g.seriesTime, seriesTimeFormat) || examMoment,
      scanType,
      seriesType,
      recognized,
      browserType,
      eye
    };
  });
}

function getOCTGroup(exam, eye, groupName) {
  if (!eye) {
    if (store.getters.currentEye) eye = store.getters.currentEye;
    else if (exam.odGroups.some(g => g.recognized)) eye = EyeTypes.OD;
    else if (exam.osGroups.some(g => g.recognized)) eye = EyeTypes.OS;
    else return null;
  }

  let groups = eye === EyeTypes.OD ? exam.odGroups : exam.osGroups;
  let recognized = groups.filter(g => g.recognized)
    .filter(g => !groupName || g.groupName === groupName);

  // if we have a browser open, prefer the same type of browser
  // so, sort by whether the browser group matches the current browser
  // g1.bt == bt && g2.bt == bt => equal
  // g1.bt != bt && g2.bt != bt => equal
  // g1.bt == bt && g2.bt != bt => g1 preferred
  // else                       => g2 preferred
  let browserType = store.getters.currentOCTBrowserType;
  if (browserType)
    recognized = recognized.sort((g1, g2) =>
      ((g1.browserType === browserType) === (g2.browserType === browserType)) ? 0 :
        g1.browserType === browserType ? -1 : 1);

  if (recognized.length > 0) {
    if (recognized[0].scanType == "star" || recognized[0].scanType == "section" || recognized[0].scanType == "radialcircles")
      recognized[0].browserType = OCTBrowserType.Spectralis;
    return recognized[0];
  }

  return null;
}

function getScanAndSeriesTypes(manufacturer, groupSeriesType) {
  if (!manufacturer || !groupSeriesType) return {};
  let scanType = ScanTypes.Unknown;
  let seriesType = SeriesTypes.Unknown;
  switch (manufacturer) {
    case Manufacturers.Zeiss:
      scanType = ScanTypes.Raster;
      if (groupSeriesType.includes("cube")) scanType = ScanTypes.Volume;
      if (groupSeriesType.includes("macula")) seriesType = SeriesTypes.Macula;
      else if (groupSeriesType.includes("anterior")
        || groupSeriesType.includes("angle")
        || groupSeriesType.includes("cornea")) seriesType = SeriesTypes.AnteriorSegment;
      else if (groupSeriesType.includes("optic")) seriesType = SeriesTypes.OpticDisc;
      break;
    case Manufacturers.Heidelberg:
      scanType = ScanTypes.Raster;
      if (groupSeriesType.includes("volume")
      || groupSeriesType.includes("cube")) scanType = ScanTypes.Volume;
      else if (groupSeriesType.includes("radialcircle")) scanType = ScanTypes.RadialCircle;
      else if (groupSeriesType.includes("circle")) scanType = ScanTypes.Circle;
      else if (groupSeriesType.includes("star")) scanType = ScanTypes.Star;
      else if (groupSeriesType.includes("section")) scanType = ScanTypes.Section;
      break;
    case Manufacturers.Optovue:
      scanType = ScanTypes.Raster;
      if (groupSeriesType.includes("macula")) {
        scanType = ScanTypes.Volume;
        seriesType = SeriesTypes.Macula;
      }
  }
  return { scanType, seriesType };
}

const recognizedTerms = ["volume", "cube", "macula", "anterior", "angle", "cornea",
  "optic", "circle", "star", "raster", "section", "radialcircles"];

function recognizedGroupSeriesType(groupSeriesType) {
  return recognizedTerms.some(t => groupSeriesType.includes(t));
}

function getOCTBrowserType(manufacturer, scanType, seriesType) {
  if (scanType === ScanTypes.Volume
    && seriesType === SeriesTypes.Macula)
    return OCTBrowserType.Cirrus;
  
  if (scanType === ScanTypes.Volume
    && seriesType === SeriesTypes.OpticDisc)
    return OCTBrowserType.Cirrus;

  if (scanType === ScanTypes.Volume
    && manufacturer === Manufacturers.Heidelberg)
    return OCTBrowserType.Spectralis;

  if (scanType === ScanTypes.Circle
    && manufacturer === Manufacturers.Heidelberg)
    return OCTBrowserType.Spectralis;

  if (scanType === ScanTypes.Section
    && manufacturer === Manufacturers.Heidelberg)
    return OCTBrowserType.Spectralis;

  if (scanType === ScanTypes.Star
    && manufacturer === Manufacturers.Heidelberg)
    return OCTBrowserType.Spectralis;

  if (scanType === ScanTypes.RadialCircle
    && manufacturer === Manufacturers.Heidelberg)
    return OCTBrowserType.Spectralis;

  if (scanType === ScanTypes.Raster) return OCTBrowserType.Raster;

  return OCTBrowserType.Unknown;
  
}